"use client"

import { useEffect, useState } from "react"
import { Check, ChevronsUpDown, Globe } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"
import { FlagIcon } from "@/components/ui/flag-icon"
import { fetchLanguageData, getLanguageInfo, getLanguageInfoSync, LanguageInfo } from "@/lib/language-utils"
import { langs, LocaleName } from "@/types"
import { Skeleton } from "../ui/skeleton"

export function LanguageSelector({ value, onChange }: {
  value: string,
  onChange: (_new: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageInfo | null>(null)
  const [languageData, setLanguageData] = useState<Record<string, LanguageInfo>>({})
  const [isLoading, setIsLoading] = useState(false)


  // Load language data from API
  useEffect(() => {
    const loadLanguageData = async () => {
      setIsLoading(true)
      try {
        // Fetch language data
        await fetchLanguageData()

        // Create a map of language info
        const langMap: Record<string, LanguageInfo> = {}

        for (const lang of langs) {
          const code = lang.value as LocaleName
          langMap[code] = await getLanguageInfo(code)
        }

        setLanguageData(langMap)
        setSelectedLanguage(langMap['en_us'])
      } catch (error) {
        console.error("Error loading language data:", error)
      } finally {
        setIsLoading(false)
      }
    }

    loadLanguageData()
  }, [])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} className="w-[250px] justify-between">
          <div className="flex items-center gap-1">
            {selectedLanguage ? (
              <>
                {isLoading ? <Skeleton className="h-4 w-24" /> : <>
                  <FlagIcon country={selectedLanguage.countryCode} />
                  <span>{selectedLanguage.nativeName}</span>
                </>}
              </>
            ) : (
              <>
                <Globe className="mr-2 h-4 w-4" />
                Select language
              </>
            )}
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[250px] p-0">
        <Command>
          <CommandInput placeholder="Search language..." />
          <CommandList>
            <CommandEmpty>No language found.</CommandEmpty>
            <CommandGroup>
              {langs.map((lang) => {
                const langCode = lang.value as LocaleName
                const langInfo =
                  isLoading || !languageData[langCode] ? getLanguageInfoSync(langCode) : languageData[langCode]
                return (
                <CommandItem
                  key={langInfo.code}
                  value={langInfo.code}
                  onSelect={() => {
                    onChange(langInfo.code)
                    setSelectedLanguage(langInfo)
                    setOpen(false)
                  }}
                >
                  <FlagIcon country={langInfo.countryCode} />
                  {isLoading ? <Skeleton className="h-4 w-24" /> : <span>{langInfo.nativeName}</span>}
                  <Check className={cn("ml-auto h-4 w-4", value === langInfo.code ? "opacity-100" : "opacity-0")} />
                </CommandItem>
              )})}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
